import { useId, useState } from 'react';
import { ImagePlus, Trash2, User } from 'lucide-react';
import './campoFoto.css';

const MAX_BYTES = 5 * 1024 * 1024;
const TIPOS = ['image/png', 'image/jpeg', 'image/webp'];
/**
 * Lado do quadrado gravado.
 *
 * 512 e não 192: a capa do card é 4:3 e mede ~350x263 CSS px numa grade de três
 * colunas. Em tela 2x isso são ~700x526 físicos, e 192 seria ampliado quase
 * quatro vezes — borrado de forma óbvia. 512 cobre o caso 2x com folga e ainda
 * cabe no teto do schema: 512x512 em WebP q0.82 dá ~40 KB, ~54 KB em base64,
 * contra o limite de 262144 caracteres.
 *
 * Subir mais do que isso não é gratuito: a coluna é lida em qualquer consulta
 * que toque a linha do agente, e a foto é servida por rota própria justamente
 * para não entrar na listagem.
 */
const LADO = 512;

/**
 * Reduz para 192x192 WebP antes de codificar.
 *
 * Sem isto, a foto que o usuário escolhe da câmera do celular tem 4000px e
 * vira ~3 MB de base64 numa coluna de texto — que depois é lida em toda
 * consulta que toque a linha. O redimensionamento acontece no cliente porque é
 * onde a imagem já está: subir 3 MB para o servidor cortar seria pagar a
 * transferência inteira para jogar 99% fora.
 *
 * O recorte é central e quadrado: a foto aparece num quadrado no card, e
 * distorcer para caber é pior do que cortar as bordas.
 */
async function reduzir(arquivo: File): Promise<string> {
  const bitmap = await createImageBitmap(arquivo);
  const lado = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = LADO;
  canvas.height = LADO;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas indisponível');
  ctx.drawImage(
    bitmap,
    (bitmap.width - lado) / 2, (bitmap.height - lado) / 2, lado, lado,
    0, 0, LADO, LADO,
  );
  bitmap.close();

  /* `toDataURL` NÃO falha quando o formato pedido não é suportado — devolve
     PNG silenciosamente. O Safari faz exatamente isso com `image/webp`, e o
     resultado foi um corpo de 107 KB numa rota que aceitava 32 KB: 413 antes
     de qualquer validação, com mensagem que não dizia nada.
     Então a verificação é sobre o que VOLTOU, não sobre o que foi pedido. */
  const webp = canvas.toDataURL('image/webp', 0.82);
  if (webp.startsWith('data:image/webp')) return webp;

  // JPEG e não PNG no fallback: para foto, PNG é várias vezes maior sem ganho
  // nenhum — e a transparência, que seria o motivo de usar PNG, é irrelevante
  // num avatar recortado em quadrado.
  return canvas.toDataURL('image/jpeg', 0.85);
}

/**
 * Seleção de imagem com pré-visualização local.
 *
 * A leitura é `FileReader` para data URL, sem upload: a pré-visualização não
 * depende de rede e nada sai da máquina antes de salvar.
 *
 * O limite de 512 KB e a lista de tipos são validados AQUI, antes de qualquer
 * envio. Deixar isso só no servidor faz o usuário esperar o upload inteiro de
 * um arquivo de 8 MB para descobrir que não serve.
 */
export function CampoFoto({
  label, valor, onChange, hint,
}: {
  label: string;
  valor: string | null;
  onChange: (dataUrl: string | null) => void;
  hint?: string;
}) {
  const id = useId();
  const [erro, setErro] = useState<string | null>(null);

  function escolher(arquivo: File | undefined) {
    setErro(null);
    if (!arquivo) return;
    if (!TIPOS.includes(arquivo.type)) {
      setErro('Use PNG, JPEG ou WebP.');
      return;
    }
    if (arquivo.size > MAX_BYTES) {
      setErro(`A imagem tem ${(arquivo.size / 1024 / 1024).toFixed(1)} MB. O limite é 5 MB.`);
      return;
    }
    void reduzir(arquivo)
      .then((dataUrl) => {
        // Barreira final antes de sair da máquina: o teto do schema é 262144
        // caracteres. Descobrir isso no servidor custa a viagem inteira.
        if (dataUrl.length > 262_144) {
          setErro('A imagem ficou grande demais mesmo depois de reduzida. Tente outra.');
          return;
        }
        onChange(dataUrl);
      })
      .catch(() => setErro('Não foi possível processar a imagem. Tente outro arquivo.'));
  }

  return (
    <div className="field">
      <span className="field__label" id={`${id}-rot`}>{label}</span>
      <div className="campo-foto">
        <span className="campo-foto__previa">
          {valor
            ? <img src={valor} alt="" />
            : <User size={28} aria-hidden="true" />}
        </span>
        <div className="campo-foto__acoes">
          <input
            id={id}
            type="file"
            className="campo-foto__entrada"
            accept={TIPOS.join(',')}
            aria-describedby={`${id}-dica`}
            onChange={(e) => escolher(e.currentTarget.files?.[0])}
          />
          <label className="campo-foto__botao" htmlFor={id}>
            <ImagePlus size={18} aria-hidden="true" />
            {valor ? 'Trocar imagem' : 'Escolher imagem'}
          </label>
          {valor && (
            <button
              type="button"
              className="campo-foto__botao"
              onClick={() => onChange(null)}
            >
              <Trash2 size={18} aria-hidden="true" />
              Remover
            </button>
          )}
        </div>
      </div>
      {erro && <p className="field__error" role="alert">{erro}</p>}
      {hint && <p className="field__hint" id={`${id}-dica`}>{hint}</p>}
    </div>
  );
}
